import React, { useCallback } from 'react';
import useSignupData from '@assetron/common/hooks/useSignupData';
import AssetronOverlayDialog from '@assetron/web-common/components/common/AssetronOverlayDialog';
import SignupCreatePassword from 'auth/src/components/signup/SignupCreatePassword';
import HeaderFooterLayout from '@assetron/web-common/layout/HeaderFooterLayout';
import {
  API_ERROR_CODES,
  EMPTY_STRING,
  INVITE_DETAILS,
} from '@assetron/common/constants/constant';
import {
  IS_USER_LOGGED_IN,
  SIGN_UP_OTP,
  USE_CUSTOM_TOKEN,
} from '@assetron/web-common/constants/LocalStorageConstants';
import { useMutation } from '@tanstack/react-query';
import { createPassword, postIdTokens } from '@assetron/common/api';
import useAuth from '@assetron/common/hooks/useAuth';
import { ResponseStatusCode } from '@assetron/common/types/responseStatus';
import { useLocalization } from '@assetron/common/contexts/localization/LocalizationContext';
import { useLocalStorage } from '@uidotdev/usehooks';
import apiErrorHandler from '@assetron/common/utils/apiErrorHandler';
import setSameSiteCookieWithoutExpiry from '@assetron/common/utils/setSameSiteCookieWithoutExpiry';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';
import {
  billingFlowLog,
  ensureBillingFlowDiagnosticsContext,
} from '@assetron/web-common/utils/billingFlowDiagnostics';
import checkAndHandlePendingCred from 'auth/src/utils/auth-helper/checkAndHandlePendingCred';
import usePasswordState from '../../../hooks/usePasswordState';
import { useNavigation } from '../../../navigation/useNavigation';

function SignupPassword() {
  const { t } = useLocalization();
  const {
    navigateToSignUpLanding,
    navigateToMainApp,
    navigateToOrganizationOnboarding,
  } = useNavigation();
  const { userEmail, userName, clearInfo } = useSignupData();
  const { signInWithEmailPassword } = useAuth();
  const { password, setPassword } = usePasswordState();
  const [signupOtp, saveSignupOtp] = useLocalStorage(SIGN_UP_OTP, EMPTY_STRING);

  const code = signupOtp;

  const postTokensMutation = useMutation({
    mutationFn: postIdTokens,
    onError: (error) => {
      apiErrorHandler(
        error,
        {
          [API_ERROR_CODES.BAD_REQUEST]: t('authErrors.invalidIdToken'),
        },
        t('apiErrorText'),
      );
    },
    retry: 3,
  });

  const createPasswordMutation = useMutation({
    mutationFn: createPassword,
    onSuccess: async (res) => {
      if (res.status === ResponseStatusCode.OK) {
        billingFlowLog('SignupPassword', 'password_created', {
          email: userEmail,
          status: res.status,
          correlationId: ensureBillingFlowDiagnosticsContext().correlationId,
        });
        const signinRes = await signInWithEmailPassword(userEmail, password);
        if (signinRes) {
          const completeSignin = async () => {
            const idToken = await signinRes.user?.getIdToken();
            await postTokensMutation.mutateAsync({
              idToken: idToken ?? EMPTY_STRING,
              refreshToken: signinRes.user?.refreshToken ?? EMPTY_STRING,
            });
            await checkAndHandlePendingCred(signinRes);
            trackGaEvent(GA_EVENTS.AUTH_SIGNUP_PASSWORD_SUCCESS, {
              sign_in_after_signup: true,
            });
            saveSignupOtp(EMPTY_STRING);
            setSameSiteCookieWithoutExpiry(IS_USER_LOGGED_IN, 'true');
            setSameSiteCookieWithoutExpiry(USE_CUSTOM_TOKEN, 'true');
            const isInviteSignupFlow = Boolean(
              globalThis.sessionStorage.getItem(INVITE_DETAILS),
            );
            if (isInviteSignupFlow) {
              navigateToMainApp();
              return;
            }
            billingFlowLog('SignupPassword', 'redirect_to_web_onboarding', {
              email: userEmail,
            });
            navigateToOrganizationOnboarding();
          };

          try {
            await completeSignin();
          } catch (error) {
            billingFlowLog('SignupPassword', 'complete_signin_failed', {
              email: userEmail,
            });
            apiErrorHandler(error, {}, t('apiErrorText'));
          }
        }
      }
    },
    onError: (error) => {
      billingFlowLog('SignupPassword', 'password_create_failed', {
        email: userEmail,
        status: error?.response?.status,
      });
      trackGaEvent(GA_EVENTS.AUTH_SIGNUP_PASSWORD_FAILURE, {
        status_code: error?.response?.status,
      });
      apiErrorHandler(
        error,
        {
          [API_ERROR_CODES.BAD_REQUEST]: t('authErrors.invalidInfo'),
          [API_ERROR_CODES.CONFLICT]: t('authErrors.somethingWentWrong'),
        },
        t('apiErrorText'),
      );
    },
  });

  const handleClick = useCallback(() => {
    billingFlowLog('SignupPassword', 'password_submit', {
      email: userEmail,
      hasOtpCode: Boolean(code),
      passwordLength: password.length,
    });
    trackGaEvent(GA_EVENTS.AUTH_SIGNUP_PASSWORD_SUBMIT, {
      password_length: password.length,
      has_otp_code: Boolean(code),
    });
    createPasswordMutation.mutate({
      email: userEmail ?? EMPTY_STRING,
      name: userName ?? EMPTY_STRING,
      password,
      code: code ?? EMPTY_STRING,
    });
  }, [code, createPasswordMutation, password, userEmail, userName]);

  function handleClose(): void {
    clearInfo();
    navigateToSignUpLanding();
  }

  return (
    <HeaderFooterLayout>
      <AssetronOverlayDialog
        open
        isLogoVisible
        content={
          <SignupCreatePassword
            password={password}
            setPassword={setPassword}
            handleClick={handleClick}
            isLoading={
              createPasswordMutation.isLoading || postTokensMutation.isLoading
            }
            showPolicyText
            title={t('createPassword.title')}
            buttonText={t('createPassword.buttonText')}
          />
        }
        onClose={() => handleClose()}
      />
    </HeaderFooterLayout>
  );
}

export default SignupPassword;
